import { useState } from "react";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, ROLE_BADGE, fmtDate } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function Profile() {
  const { user, profile } = useAuth();
  const [open, setOpen] = useState(false);
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");

  const change = async () => {
    if (newPw.length < 6) return toast.error("Kata sandi baru minimal 6 karakter");
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Kata sandi diperbarui");
      setOpen(false); setOldPw(""); setNewPw("");
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h1 className="font-heading text-2xl font-bold tracking-tight lg:text-3xl">Profil Saya</h1>

      <Card className="rounded-2xl border-slate-200 p-6 dark:border-slate-800">
        <div className="flex flex-wrap items-center gap-5">
          <Avatar className="h-20 w-20">
            <AvatarImage src={profile?.avatar} />
            <AvatarFallback className="bg-primary/15 text-2xl text-primary">{profile?.full_name?.[0]}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-heading text-xl font-bold">{profile?.full_name}</p>
            <span className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${ROLE_BADGE[user.role]}`}>{ROLE_LABELS[user.role]}</span>
          </div>
          <Button variant="outline" className="ml-auto gap-2" onClick={() => setOpen(true)} data-testid="open-change-password">
            <KeyRound className="h-4 w-4" /> Ganti Kata Sandi
          </Button>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 border-t border-slate-100 pt-6 dark:border-slate-800 sm:grid-cols-2">
          <Item label="ID Karyawan" value={profile?.employee_id} mono />
          <Item label="NIK" value={profile?.nik_masked} mono />
          <Item label="Departemen" value={profile?.department_name} />
          <Item label="Jabatan" value={profile?.position_name} />
          <Item label="Status Kepegawaian" value={profile?.employment_status} />
          <Item label="Tanggal Bergabung" value={fmtDate(profile?.join_date)} />
          <Item label="Lokasi Kerja" value={profile?.work_location} />
          <Item label="Jadwal Kerja" value={profile?.work_schedule} />
          <Item label="Telepon" value={profile?.phone} />
          <Item label="Email" value={profile?.email || "-"} />
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ganti Kata Sandi</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Kata Sandi Lama</Label>
              <Input type="password" data-testid="profile-old-password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} /></div>
            <div className="space-y-2"><Label>Kata Sandi Baru</Label>
              <Input type="password" data-testid="profile-new-password" value={newPw} onChange={(e) => setNewPw(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={change} data-testid="profile-save-password">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Item({ label, value, mono }) {
  return (
    <div>
      <p className="text-xs text-slate-400">{label}</p>
      <p className={`font-medium ${mono ? "font-mono" : ""}`}>{value || "-"}</p>
    </div>
  );
}
